"use client";
import type { GameData } from "@/app/games/(details)/types";
import { useGameDetails } from "@/app/games/(details)/useGameDetails";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGamePageV3Analytics } from "@/context/AnalyticsProvider/gamePageV3";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";

interface FollowCTAProps {
  prizeImage?: string;
  gameData?: GameData;
}

const formatTimeUnit = (unit: number): string => {
  return unit.toString().padStart(2, "0");
};

const formatHours = (hours: number): string => {
  if (hours < 100) {
    return formatTimeUnit(hours);
  }
  return hours.toString();
};

const formatDays = (days: number): string => {
  if (days < 100) {
    return formatTimeUnit(days);
  }
  return days.toString();
};

const parseISOTimestamp = (timestamp: string | undefined): number | null => {
  if (!timestamp) return null;

  const isoDate = Date.parse(timestamp);
  if (!Number.isNaN(isoDate)) {
    return isoDate;
  }

  return null;
};

const calculateTimeRemaining = (
  endTimestamp: string | undefined,
): { days: number; hours: number } => {
  const endTime = parseISOTimestamp(endTimestamp);
  if (!endTime) {
    return { days: 0, hours: 0 };
  }

  const now = Date.now();
  const diffMs = endTime - now;

  if (diffMs <= 0) {
    return { days: 0, hours: 0 };
  }

  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  return { days, hours };
};

export const FollowCTA = ({
  prizeImage = "/images/games/8ball/prize.png",
  gameData,
}: FollowCTAProps) => {
  const { track } = useGamePageV3Analytics();
  const { isWishlisted, addToWishlist } = useGameDetails(gameData as GameData);

  const endTimestamp = gameData?.gameContent?.follow_reward_end_timestamp;
  const showCountdown = !!endTimestamp;

  const timeFromTimestamp = useMemo(
    () => calculateTimeRemaining(endTimestamp),
    [endTimestamp],
  );

  const [currentDays, setCurrentDays] = useState(timeFromTimestamp.days);
  const [currentHours, setCurrentHours] = useState(timeFromTimestamp.hours);

  const disabled = isWishlisted;

  // Timer that recalculates from timestamp every minute
  useEffect(() => {
    if (disabled || !showCountdown || !endTimestamp) return;

    const interval = setInterval(() => {
      const { days, hours } = calculateTimeRemaining(endTimestamp);
      setCurrentDays(days);
      setCurrentHours(hours);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [disabled, showCountdown, endTimestamp]);

  const handleClick = () => {
    if (disabled) return;
    if (gameData?.details.game_id) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "follow-cta",
          control: "FollowCTA",
          controlType: ControlType.BUTTON,
          gameId: gameData.details.game_id,
          gameName: gameData.details.name,
        },
      });
    }
    addToWishlist();
  };

  const daysStr = formatDays(currentDays);
  const hoursStr = formatHours(currentHours);

  // When there’s no countdown, centre the left section
  const centerLeft = !showCountdown;

  return (
    <div className="!mt-14 relative flex flex-col gap-1 items-center justify-center w-full font-suisse">
      {/* Button Container */}
      <div className="gradient-border backdrop-blur-xl @lg:px-[12.5rem] @lg:py-[2.1875rem] px-[9rem] py-[1.75rem]">
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          className={cn(
            "group relative overflow-hidden",
            "absolute inset-0 z-10",
            "flex items-center gap-[0.3125rem] px-4 py-2",
            centerLeft ? "justify-center" : "justify-between",
            "rounded-full border border-white/20 cursor-pointer",
            "text-white text-xs @lg:text-base",
            "[text-shadow:0_0.0625rem_0.125rem_rgba(0,0,0,0.5)]",
            "transition-all duration-300 ease-in-out",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        >
          {/* hover gradient overlay */}
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-0",
              "opacity-0 group-hover:opacity-100",
              "transition-opacity duration-300 ease-in-out",
              "bg-[radial-gradient(60%_60%_at_50%_100%,_rgba(var(--accent-colour),_0.40)_0%,_rgba(var(--accent-colour),_0.20)_30%,_rgba(var(--accent-colour),_0.10)_50%,_transparent_80%)]",
            )}
          />

          {/* Content row */}
          <div
            className={cn(
              "relative z-10 flex w-full items-stretch px-5",
              centerLeft ? "justify-center" : "",
            )}
          >
            {/* Left Section */}
            <div
              className={cn(
                "flex flex-col gap-1 h-8 min-w-0",
                centerLeft
                  ? "items-center text-center mx-auto"
                  : "flex-1 items-center text-left",
              )}
            >
              <div
                className={cn(
                  "flex w-full items-start",
                  centerLeft ? "justify-center" : "justify-start",
                )}
              >
                <p
                  className={cn(
                    "leading-none text-foreground font-semibold",
                    centerLeft ? "" : "flex-1",
                  )}
                >
                  Follow Now
                </p>
              </div>
              <div
                className={cn(
                  "flex items-center w-full",
                  centerLeft ? "justify-center" : "",
                )}
              >
                <p className="leading-none text-xs font-normal text-foreground whitespace-nowrap">
                  Exclusive early rewards
                </p>
              </div>
            </div>

            {/* Divider */}
            {showCountdown && (
              <div className=" mx-5 self-stretch w-px shrink-0 bg-[radial-gradient(circle,_white,_transparent)]" />
            )}

            {/* Right Section - Countdown */}
            {showCountdown && (
              <div className="flex flex-col gap-1 h-8 items-end shrink-0">
                {/* Countdown Numbers */}
                <div className="flex gap-0.5 items-start">
                  {/* Days */}
                  <div className="flex items-center leading-none font-semibold text-center text-foreground">
                    <span>{daysStr}</span>
                    <span>D</span>
                  </div>
                  <span className="leading-none font-semibold text-center text-foreground">
                    :
                  </span>
                  {/* Hours */}
                  <div className="leading-none font-semibold text-center text-foreground">
                    <span>{hoursStr}H</span>
                  </div>
                </div>
                {/* Hours left */}
                <div className="flex gap-2 items-center justify-end w-full">
                  <p className="text-xs leading-none font-normal text-foreground">
                    Hours left
                  </p>
                </div>
              </div>
            )}
          </div>
        </button>
      </div>

      {/* Prize Image */}
      <div
        className={cn(
          "absolute left-1/2 -translate-x-1/2",
          "@lg:size-[5rem] size-16",
          "pointer-events-none",
          showCountdown ? "top-[-2.25rem]" : "top-[-4rem]",
        )}
      >
        <Image
          src={prizeImage}
          alt="Prize"
          width={81}
          height={81}
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
      </div>
    </div>
  );
};
